'use client';

import type { ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import React, { Component } from 'react';

import { Alert } from '../alert';
import { Button } from '../button';
import { Center } from '../layout';
import { ErrorReportingContext } from './error-reporting-context';
import type { ErrorReportingHandler } from './error-reporting-context';

export type ErrorBoundaryOnError = ErrorReportingHandler;
export {
  ErrorReportingProvider,
  useErrorReporting,
  type ErrorReportingHandler,
} from './error-reporting-context';

interface ErrorBoundaryOptions {
  componentName?: string;
  title?: string;
  onError?: ErrorBoundaryOnError;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  options?: ErrorBoundaryOptions;
  onError?: ErrorBoundaryOnError;
}

interface State {
  hasError: boolean;
}

const DEFAULT_TITLE = 'Oops, our mistake!';

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  static contextType = ErrorReportingContext;

  declare context: ErrorReportingHandler | null;

  public state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(_: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const onError = this.props.onError ?? this.props.options?.onError ?? this.context;
    onError?.(error, errorInfo, this.props.options?.componentName);
  }

  handleTryAgain = () => {
    this.setState({ hasError: false });
  };

  render() {
    const { options } = this.props;
    // Check if the error is thrown
    if (this.state.hasError) {
      return (
        <Center>
          <Alert color="red" title={options?.title ?? DEFAULT_TITLE}>
            Sorry, we ran into an issue rendering{' '}
            {options?.componentName ? `the ${options?.componentName}` : 'this'} component. Please
            refresh the page, or{' '}
            <Button color="red" onClick={this.handleTryAgain} size="xs">
              try again
            </Button>
          </Alert>
        </Center>
      );
    }

    // Return children components in case of no error

    return this.props.children;
  }
}

/**
 * withErrorBoundary
 * ---
 * @description
 * Wraps a component with an error boundary.
 * @example
 * ```tsx
 * const MyComponent = withErrorBoundary(MyComponent, { componentName: 'MyComponent' });
 * ```
 * @param WrappedComponent - The component to wrap.
 * @param options - The options for the error boundary.
 * @param options.componentName - The name of the component to wrap.
 * @param options.title - The title of the error boundary.
 * @param options.onError - Optional callback when a child throws (e.g. app telemetry).
 * @returns The wrapped component.
 */
export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: ErrorBoundaryOptions,
): React.ComponentType<PropsWithChildren<P & Partial<ErrorBoundaryProps>>> => {
  const ErrorBoundaryWrapper: React.ComponentType<
    PropsWithChildren<P & Partial<ErrorBoundaryProps>>
  > = (props) => {
    const { onError: onErrorProp, ...wrappedProps } = props;
    const onError = onErrorProp ?? options?.onError;

    return (
      <ErrorBoundary onError={onError} options={options}>
        <WrappedComponent {...(wrappedProps as P)} />
      </ErrorBoundary>
    );
  };
  return ErrorBoundaryWrapper;
};
